import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaHeart, FaStar } from 'react-icons/fa';
import Footer from '../component/common/Footer';
import { TEMPLES_DATA } from '../Utils/Constants';
import { useLanguage } from '../i18n/LanguageContext';
import { useDynamicTranslation } from '../Utils/translateDynamicData';

const TemplePage = () => {
  const { t, language } = useLanguage();
  const { translateTempleData } = useDynamicTranslation(language);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [filteredTemples, setFilteredTemples] = useState([]);

  useEffect(() => {
    let filtered = TEMPLES_DATA;
    
    if (searchTerm) {
      filtered = filtered.filter(temple => 
        temple.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        temple.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        temple.deity.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedFilter !== 'all') {
      filtered = filtered.filter(temple => {
        const deity = temple.deity.toLowerCase();
        switch (selectedFilter) {
          case 'shiva':
            return deity.includes('shiva');
          case 'vishnu':
            return deity.includes('vishnu') || deity.includes('jagannath') || deity.includes('venkateswara');
          case 'devi':
            return deity.includes('goddess') || deity.includes('meenakshi') || deity.includes('durga');
          default:
            return true;
        }
      });
    }

    setFilteredTemples(filtered);
  }, [searchTerm, selectedFilter]);

  const handleTempleClick = (templeId) => {
    navigate(`/temples/${templeId}`);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="text-yellow-400" />);
    }

    return stars;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br mt-10 from-orange-50 to-amber-50">
      {/* Page Header */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">{t('sacredTemples')}</h1>
          <p className="text-xl md:text-2xl text-orange-100 max-w-3xl mx-auto">
            {t('discoverDivineArchitecture')}
          </p>
                </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder={t('searchTemplesPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-4 pl-12 border-2 border-orange-200 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-orange-500" />
      </div>

              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedFilter('all')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${
                    selectedFilter === 'all'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                      : 'bg-white text-orange-600 border-2 border-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {t('allTemples')}
                </button>
                <button
                  onClick={() => setSelectedFilter('shiva')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${
                    selectedFilter === 'shiva'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                      : 'bg-white text-orange-600 border-2 border-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {t('shiva')}
                </button>
                <button
                  onClick={() => setSelectedFilter('vishnu')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${
                    selectedFilter === 'vishnu'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                      : 'bg-white text-orange-600 border-2 border-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {t('vishnu')}
                </button>
                <button
                  onClick={() => setSelectedFilter('devi')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${
                    selectedFilter === 'devi'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                      : 'bg-white text-orange-600 border-2 border-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {t('devi')}
                </button>
      </div>
            </div>
          </div>
        </div>
      </section>


      {/* Temple Grid */}
      <section className="py-12 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-orange-800 mb-8 text-center">{t('allSacredTemples')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTemples.map((temple) => {
              const translatedTemple = translateTempleData(temple);
              return (
              <div
                key={temple.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
                onClick={() => handleTempleClick(temple.id)}
              >
                <div className="relative">
                  <img
                    src={temple.image}
                    alt={translatedTemple.name}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {t('comingSoon')}
                  </div>
                  <div className="absolute bottom-4 right-4">
                    <button className="bg-white bg-opacity-90 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold hover:bg-opacity-100 transition-all">
                      <FaHeart className="inline mr-1" />
                      {t('save')}
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-orange-800">{translatedTemple.name}</h3>
                    <div className="flex items-center space-x-1 text-yellow-500">
                      {renderStars(temple.rating)}
                      <span className="text-gray-600 text-sm">({temple.rating})</span>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{translatedTemple.location}</p>
                  <p className="text-gray-700 text-sm mb-4">{translatedTemple.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-orange-600 font-semibold text-sm">
                      {temple.emoji} {translatedTemple.deity}
                    </span>
                    <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full text-sm font-semibold hover:from-orange-600 hover:to-red-600 transition-all">
                      {t('explore')}
                    </button>
      </div>
                </div>
        </div>
              );
            })}
      </div>

          {/* Load More Button */}
          {/* <div className="text-center mt-12">
            <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg">
              {t('loadMoreTemples')}
            </button>
           </div> */}
      </div>
      </section>

      <Footer />
    </div>
  );
};

export default TemplePage;